import { useState, useEffect } from "react";
import { supabase } from "../supabase";
import { Link } from "react-router-dom";
import "../styles.css";

function formatDate(dateStr) {
  if (!dateStr) return <span className="italic text-gray-400">Not available</span>;
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  } catch {
    return <span className="italic text-gray-400">Invalid</span>;
  }
}

// Badge component
const TierBadge = ({ tier }) => {
  if (tier === "choir_supporter")
    return (
      <span className="ml-2 badge-supporter animate-bounce">Supporter</span>
    );
  if (tier === "choir_expert")
    return (
      <span className="ml-2 badge-expert animate-pulse">Expert</span>
    );
  return null;
};

export default function Profile({ user }) {
  const [profile, setProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // For download progress for choir member
  const DOWNLOAD_LIMIT = 1;

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);

    // Fetch profile + admin
    Promise.all([
      supabase.from("profiles").select("*").eq("id", user.id).single(),
      supabase.from("admins").select("user_id").eq("user_id", user.id).single()
    ])
      .then(([profileRes, adminRes]) => {
        setProfile(profileRes.data || null);
        setIsAdmin(!!(adminRes.data && !adminRes.error));
        setLoading(false);
      });
  }, [user]);

  // States
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-950 via-green-100 to-white px-2">
        <div className="bg-white/90 rounded-2xl shadow-2xl px-6 py-10 border border-green-200 max-w-md w-full animate-fade-in">
          <h2 className="text-3xl font-extrabold text-green-900 mb-6 text-center">
            Please log in to view your profile.
          </h2>
          <Link
            to="/login"
            className="inline-block px-7 py-2 bg-gradient-to-r from-green-900 to-green-500 text-white rounded-full font-semibold shadow-lg hover:from-green-800 hover:to-green-600 transition-all"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="w-14 h-14 rounded-full border-8 border-green-200 border-t-green-700 animate-spin mb-4"></div>
        <div className="text-green-700 font-semibold">Loading your profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-gradient-to-br from-green-50 via-lime-100 to-white px-2">
        <div className="bg-white/90 rounded-3xl shadow-2xl p-10 border border-green-200 max-w-md w-full animate-fade-in">
          <h2 className="text-2xl font-extrabold text-green-900 text-center">
            Profile not found.
          </h2>
        </div>
      </div>
    );
  }

  // Personalized Greeting (extract first name)
  const firstName = (profile.full_name || "").split(" ")[0];

  // Tier details
  let tier = profile.subscription_tier || "choir_member";
  let tierLabel = "Choir Member";
  let tierDesc = "Free Forever";
  let tierColor = "from-green-800 to-green-600 text-white";
  let tierAnim = "hover:scale-105 shadow-green-300/20";
  let tierPrice = "₦0/mo";
  if (tier === "choir_supporter") {
    tierLabel = "Choir Supporter";
    tierDesc = "All access for 1 month";
    tierColor = "from-green-700 to-lime-400 text-green-900";
    tierAnim = "hover:scale-105 shadow-lime-300/20";
    tierPrice = "₦1,000/mo";
  }
  if (tier === "choir_expert") {
    tierLabel = "Choir Expert";
    tierDesc = "All access for 1 year";
    tierColor = "from-yellow-400 to-green-200 text-yellow-900";
    tierAnim = "hover:scale-105 shadow-yellow-200/40";
    tierPrice = "₦7,500/yr";
  }
  if (isAdmin) {
    tierLabel = "Admin";
    tierDesc = "Unlimited Access Forever";
    tierColor = "from-green-900 to-green-700 text-white";
    tierAnim = "hover:scale-110 shadow-green-700/30";
    tierPrice = "—";
  }

  // Download usage logic for choir_member
  const downloadCount = profile.download_count_month || 0;
  const downloadBarPercent = Math.min(100, (downloadCount / DOWNLOAD_LIMIT) * 100);
  const downloadsLeft = Math.max(0, DOWNLOAD_LIMIT - downloadCount);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-950 via-green-100 to-white py-10 flex items-center justify-center px-2 sm:px-4">
      <div
        className={`w-full max-w-2xl mx-auto bg-white/95 rounded-3xl shadow-2xl px-4 sm:px-10 py-8 border border-green-100 animate-fade-in transition-all duration-700`}
      >
        {/* Stylish Greeting */}
        <div className="mb-6 w-full flex justify-center">
          <h1
            className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-green-900 via-green-600 to-lime-400 text-transparent bg-clip-text animate-fade-in-slow"
            style={{ letterSpacing: "0.02em" }}
          >
            {`Hello${firstName ? `, ${firstName}` : ""}!`}
            <span className="ml-2 inline-block animate-wave">👋</span>
          </h1>
        </div>
        {/* Tier card with badge */}
        <div className="flex flex-col gap-4 items-center sm:items-start sm:flex-row sm:gap-10 mb-6">
          <div
            className={`rounded-2xl px-6 py-4 bg-gradient-to-br ${tierColor} shadow-xl font-extrabold text-lg sm:text-2xl flex flex-col items-center justify-center ${tierAnim} transition-all duration-300 relative`}
            style={{ minWidth: 170 }}
          >
            <span className="tracking-wider flex items-center">
              {tierLabel}
              <TierBadge tier={tier} />
            </span>
            <span className="mt-1 text-xs font-medium text-white/90">{tierDesc}</span>
            <span className="mt-2 text-base font-semibold">{tierPrice}</span>
          </div>
          <div className="flex-1 w-full">
            <h2 className="text-2xl sm:text-3xl font-black text-green-900 mb-1">My Profile</h2>
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-green-900 text-base">
              <div>
                <span className="font-bold text-green-700 mr-2">Full Name:</span>
                <span>{profile.full_name || <span className="italic text-gray-400">Not provided</span>}</span>
              </div>
              <div>
                <span className="font-bold text-green-700 mr-2">Email:</span>
                <span>{profile.email || <span className="italic text-gray-400">Not provided</span>}</span>
              </div>
              <div>
                <span className="font-bold text-green-700 mr-2">Phone:</span>
                <span>{profile.phone_number || <span className="italic text-gray-400">Not provided</span>}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2 mt-3 text-green-900 text-base">
              <div>
                <span className="font-bold text-green-700 mr-2">Registered:</span>
                <span>{formatDate(profile.created_at)}</span>
              </div>
              {profile.subscribed_at && (
                <div>
                  <span className="font-bold text-green-700 mr-2">Subscribed:</span>
                  <span>{formatDate(profile.subscribed_at)}</span>
                </div>
              )}
              {profile.subscription_expires_at && (
                <div>
                  <span className="font-bold text-green-700 mr-2">Expires:</span>
                  <span>{formatDate(profile.subscription_expires_at)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="mt-7">
          {/* Download info */}
          {isAdmin ? (
            <div className="flex items-center justify-center bg-gradient-to-r from-green-900 to-green-600 rounded-xl py-4 px-8 shadow-lg animate-pulse">
              <span className="text-white text-lg font-bold tracking-wide">Unlimited downloads. Forever.</span>
            </div>
          ) : tier === "choir_member" ? (
            <div>
              <div className="flex justify-between items-end mb-1">
                <span className="font-semibold text-green-800">
                  You have <span className="font-bold">{downloadsLeft}</span> download{downloadsLeft !== 1 ? "s" : ""} remaining this month.
                </span>
                <span className={`text-xs font-medium ${downloadCount >= DOWNLOAD_LIMIT ? "text-rose-600" : "text-green-700"}`}>
                  {downloadCount >= DOWNLOAD_LIMIT ? "Limit reached" : `${downloadCount}/${DOWNLOAD_LIMIT} used`}
                </span>
              </div>
              <div className="relative h-5 rounded-lg bg-green-100 overflow-hidden shadow-inner">
                <div
                  className={`h-full transition-all duration-700 rounded-lg ${downloadCount < DOWNLOAD_LIMIT ? "bg-gradient-to-r from-green-700 to-green-400" : "bg-gradient-to-r from-rose-600 to-rose-400 animate-pulse"}`}
                  style={{ width: `${downloadBarPercent}%` }}
                />
              </div>
              <div className="text-xs mt-2 text-green-800">
                Upgrade for unlimited downloads &amp; more features!
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center bg-gradient-to-r from-green-700 via-green-400 to-lime-200 rounded-xl py-4 px-8 shadow animate-fade-in">
              <span className="text-green-900 font-bold text-lg">Unlimited downloads while your subscription is active.</span>
            </div>
          )}
        </div>
        <div className="mt-10 flex justify-center">
          <Link
            to="/"
            className="inline-block px-7 py-2.5 rounded-full font-bold bg-gradient-to-r from-green-900 via-green-500 to-lime-400 text-white shadow-lg hover:from-green-900 hover:to-green-600 hover:scale-105 transition-all"
          >
            Back to Home
          </Link>
        </div>
        {/* Upgrade Buttons */}
        {!isAdmin && (
          <div className="mt-6 flex justify-center gap-3">
            {(tier === "choir_member" || !tier) && (
              <Link
                to="/subscribe"
                className="block px-8 py-2.5 rounded-full font-bold bg-gradient-to-r from-green-800 via-lime-400 to-green-400 text-white shadow-xl hover:scale-105 hover:from-green-700 hover:to-lime-500 transition-all text-base"
              >
                Upgrade Subscription
              </Link>
            )}
            {tier === "choir_supporter" && (
              <a
                href={`https://paystack.shop/pay/choircenterexpert?callback_url=https://choircenter.com/thank-you?user_id=${profile.id}&tier=choir_expert`}
                className="block px-8 py-2.5 rounded-full font-bold bg-gradient-to-br from-yellow-400 via-yellow-200 to-yellow-50 text-yellow-900 shadow-xl hover:scale-105 hover:from-yellow-600 hover:to-yellow-200 transition-all text-base"
              >
                Upgrade to Choir Expert
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
